import { createAsyncThunk } from '@reduxjs/toolkit';

import { getErrorMessage } from '@/shared/utils/error';

import type {
  CreateSectorPayload,
  InviteRequestPayload,
  MakePrivatePayload,
  RemovePersonPayload,
  SectorToggleRequestPayload,
  ShareItemModule,
  UpdatePermissionPayload,
  UpdateSectorPayload,
} from '../types/compartilhamentoTypes';

import { compartilhamentoService } from './services';

export const loadShareContext = createAsyncThunk(
  'compartilhamento/loadContext',
  async (
    payload: { module: ShareItemModule; itemId: string; title?: string },
    { rejectWithValue },
  ) => {
    try {
      return await compartilhamentoService.getItemContext(
        payload.module,
        payload.itemId,
        payload.title,
      );
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const searchShareSuggestions = createAsyncThunk(
  'compartilhamento/searchSuggestions',
  async (query: string, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.searchSuggestions(query);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const invitePerson = createAsyncThunk(
  'compartilhamento/invitePerson',
  async (payload: InviteRequestPayload, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.invitePerson(payload);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const updatePersonPermission = createAsyncThunk(
  'compartilhamento/updatePersonPermission',
  async (payload: UpdatePermissionPayload, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.updatePermission(payload);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const removePerson = createAsyncThunk(
  'compartilhamento/removePerson',
  async (payload: RemovePersonPayload, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.removePerson(payload);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const toggleSectorAccess = createAsyncThunk(
  'compartilhamento/toggleSectorAccess',
  async (payload: SectorToggleRequestPayload, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.toggleSectorAccess(payload);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const makeItemPrivate = createAsyncThunk(
  'compartilhamento/makePrivate',
  async (payload: MakePrivatePayload, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.makePrivate(payload);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const generateInviteLink = createAsyncThunk(
  'compartilhamento/generateInviteLink',
  async (payload: { module: ShareItemModule; itemId: string }, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.generateInviteLink(payload.module, payload.itemId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const listSectorsSummary = createAsyncThunk(
  'compartilhamento/listSectorsSummary',
  async (_: void, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.listSectorsSummary();
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const createSector = createAsyncThunk(
  'compartilhamento/createSector',
  async (payload: CreateSectorPayload, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.createSector(payload);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const updateSector = createAsyncThunk(
  'compartilhamento/updateSector',
  async (payload: UpdateSectorPayload, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.updateSector(payload);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const deleteSector = createAsyncThunk(
  'compartilhamento/deleteSector',
  async (id: string, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.deleteSector(id);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const loadInvitePreview = createAsyncThunk(
  'compartilhamento/loadInvitePreview',
  async (token: string, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.getInvitePreview(token);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const acceptInvite = createAsyncThunk(
  'compartilhamento/acceptInvite',
  async (
    payload: { token: string; fullName?: string; email?: string; cpf?: string },
    { rejectWithValue },
  ) => {
    try {
      return await compartilhamentoService.acceptInvite(payload.token, {
        fullName: payload.fullName,
        email: payload.email,
        cpf: payload.cpf,
      });
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const declineInvite = createAsyncThunk(
  'compartilhamento/declineInvite',
  async (token: string, { rejectWithValue }) => {
    try {
      return await compartilhamentoService.declineInvite(token);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);
